import { COMMON, ONBOARDING } from '@constants';
import { FlowPageFragment } from '@graphql/generated/graphql';
import { useFlow } from '@lib/pimster-flow/contexts/FlowContext';
import { useTranslation } from 'next-i18next';
import {
    createContext,
    FC,
    ReactNode,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react';

import { Alignment, Process } from '../types';
import {
    getCtaText,
    getEscapeText,
    getDescriptionAlignment,
    getHeadingAlignment,
} from '../utils/getters';
import { isFlowPageWithLoader } from '../utils/validators';

interface IFlowPageContext {
    flowPage: FlowPageFragment;
    title?: string;
    description?: string;
    headingAlignment: Alignment;
    descriptionAlignment: Alignment;
    ctaText: string;
    escapeText: string;
    process?: Process;
}

export const FlowPageContext = createContext<IFlowPageContext>(
    {} as IFlowPageContext
);

interface Props {
    children: ReactNode;
    flowPage: FlowPageFragment;
}

export const FlowPageProvider: FC<Props> = ({ children, flowPage }) => {
    const { t } = useTranslation([ONBOARDING, COMMON]);
    const { currentStep } = useFlow();
    const [process, setProcess] = useState<Process | undefined>(undefined);

    useEffect(() => {
        if (isFlowPageWithLoader(flowPage)) {
            setProcess({
                isLoading: true,
                title: flowPage.Loader.Title,
                description: flowPage.Loader.Description,
            });
            setTimeout(() => setProcess(undefined), flowPage.Loader.Duration);
        }
    }, [flowPage]);

    const title = useMemo(
        () => (process ? process.title : flowPage.Title),
        [process, flowPage.Title]
    );
    const description = useMemo(
        () => (process ? process.description : flowPage.Description),
        [process, flowPage.Description]
    );
    const headingAlignment = useMemo(
        () => getHeadingAlignment(flowPage),
        [flowPage]
    );
    const descriptionAlignment = useMemo(
        () => getDescriptionAlignment(flowPage),
        [flowPage]
    );
    const ctaText = useMemo(
        () => getCtaText(t, currentStep, flowPage),
        [t, currentStep, flowPage]
    );
    const escapeText = useMemo(
        () => getEscapeText(t, currentStep),
        [t, currentStep]
    );

    return (
        <FlowPageContext.Provider
            value={{
                flowPage,
                title,
                description,
                headingAlignment,
                descriptionAlignment,
                ctaText,
                escapeText,
                process,
            }}
        >
            {children}
        </FlowPageContext.Provider>
    );
};

export const useFlowPage = () => useContext(FlowPageContext);
